import { createTheme, ThemeOptions } from '@mui/material/styles';

import { FONT_FAMILY } from '../assets/styles/commonStyles';
import { COMMON_COLORS, LIGHT_MODE_COLORS } from '../assets/styles/colors';

// Extend the default Theme type
declare module '@mui/material/styles' {
  interface Theme {
    custom: {
      borderRadius: {
        small: string;
        medium: string;
        large: string;
        xLarge: string;
        [key: string]: string;
      };
      margin: {
        xxSmall: string;
        xSmall: string;
        small: string;
        medium: string;
        mediumX: string;
        mediumXX: string;
        large: string;
        largeX: string;
        largeXX: string;
        [key: string]: string;
      };
      padding: {
        xxSmall: string;
        xSmall: string;
        small: string;
        smallMedium: string;
        medium: string;
        mediumX: string;
        mediumXX: string;
        large: string;
        largeX: string;
        largeXX: string;
        largeXXX: string;
        [key: string]: string;
      };
      gap: {
        xSmall: string;
        small: string;
        medium: string;
        large: string;
        [key: string]: string;
      };
      width: {
        [key: string]: string;
      };
      height: {
        [key: string]: string;
      };
      customColors: {
        [key: string]: string;
      };
      gradients: {
        [key: string]: string;
      };
      zIndex: {
        [key: string]: number;
      };
      emptyStateBackground?: string;
      brandTokens?: import('./brands/brandTokens').BrandSemanticTokens;
    };
    typography: {
      lineHeight: {
        micro: string;
        xSmall: string;
        small: string;
        [key: string]: string;
      };
      fontWeight: {
        xSmall: number;
        small: number;
        normal: number;
        medium: number;
        bold: number;
      };
      textSize: {
        xxSmall: string;
        xSmall: string;
        small: string;
        smallMedium: string;
        medium: string;
        mediumX: string;
        mediumXX: string;
        large: string;
        largeX: string;
        largeXX: string;
        largeXXX: string;
        [key: string]: string;
      };
    };
  }

  interface TypeText {
    tertiary: string;
    quaternary: string;
    quinary: string;
    senary: string;
    septenary: string;
    octonary: string;
    nonary: string;
    denary: string;
    elevenary: string;
    [key: string]: string;
  }

  interface TypeBackground {
    secondary: string;
    tertiary: string;
    quaternary: string;
    quinary: string;
    senary: string;
    septenary: string;
    octonary: string;
    nonary: string;
    denary: string;
    elevenary: string;
    twelvenary: string;
    thirteenary: string;
    fourteenary: string;
    fifteenary: string;
    sixteenary: string;
    seventeenary: string;
    eighteenary: string;
    nineteenary: string;
    twenteenary: string;
    twentyOnenary: string;
    twentyTwoary: string;
    grey_200: string;
    grey_300: string;
    dark_grey_100: string;
    black_1100: string;
    black_1200: string;
    black: string;
    YELLOW: string;
  }

  interface Palette {
    tertiary?: PaletteColor;
    quinary?: PaletteColor;
  }

  interface PaletteOptions {
    tertiary?: object;
    quaternary?: object;
    quinary?: object;
    senary?: object;
  }

  interface PaletteColor {
    yellow?: string;
    yellow_100?: string;
    yellow_200?: string;
    yellow_300?: string;
    yellow_400?: string;
    yellow_500?: string;
    yellow_600?: string;
    yellow_700?: string;
    yellow_800?: string;
    yellow_900?: string;
    green_200?: string;
    green?: string;
    red_100?: string;
    red_200?: string;
    red_300?: string;
    red_400?: string;
    primary?: string;
    secondary?: string;
    tertiary?: string;
    quaternary?: string;
    quinary?: string;
    senary?: string;
    septenary?: string;
  }
}

const commonSettings = {
  typography: {
    fontFamily: FONT_FAMILY,
    h1: {
      fontSize: '2.5rem',
    },
    h2: {
      fontSize: '2rem',
    },
    lineHeight: {
      micro: '1.35969rem',
      xSmall: '1.25rem',
      small: '1.53825rem',
      '1_125': '1.125rem',
      '1_31': '1.31rem',
      '2_25': '2.25rem',
      '2_625': '2.625rem',
      '2_8': '2.8rem',
    },
    fontWeight: {
      xSmall: 300,
      small: 400,
      normal: 500,
      medium: 600,
      bold: 700,
    },
    textSize: {
      xxSmall: '0.25rem',
      xSmall: '0.6875rem',
      small: '0.75rem',
      medium: '1rem',
      mediumX: '1.25rem',
      mediumXX: '1.5rem',
      large: '1.75rem',
      largeX: '2rem',
      largeXX: '2.25rem',
      largeXXX: '2.5rem',
      '0_625': '0.625rem',
      '0_7': '0.7rem',
      '0_875': '0.875rem',
      '1_125': '1.125rem',
      '1_875': '1.875rem',
      '3': '3rem',
      '3_5': '3.5rem',
      '4_5': '4.5rem',
    },
  },
  custom: {
    margin: {
      xxSmall: '0.25rem',
      xSmall: '0.5rem',
      small: '0.75rem',
      medium: '1rem',
      mediumX: '1.25rem',
      mediumXX: '1.5rem',
      large: '1.75rem',
      largeX: '2rem',
      largeXX: '2.25rem',
      largeXXX: '2.5rem',
      '0_125': '0.125rem',
      '0_375': '0.375rem',
      '0_87': '0.87rem',
      '2_63': '2.63rem',
      '2_75': '2.75rem',
      '3': '3rem',
      '3_5': '3.5rem',
      '4_5': '4.5rem',
      '5_75': '5.75rem',
      '8': '8rem',
    },
    padding: {
      xxSmall: '0.25rem',
      small: '0.5rem',
      smallMedium: '0.75rem',
      medium: '1rem',
      mediumX: '1.5rem',
      large: '1.75rem',
      largeX: '2rem',
      largeXX: '2.25rem',
      '0_125': '0.125rem',
      '0_3125': '0.3125rem',
      '0_375': '0.375rem',
      '0_44': '0.44rem',
      '0_625': '0.625rem',
      '0_79': '0.79rem',
      '0_875': '0.875rem',
      '1_12': '1.12rem',
      '1_25': '1.25rem',
      '2_06': '2.06rem',
      '2_5': '2.5rem',
      '3': '3rem',
      '3_5': '3.5rem',
      '4': '4rem',
      '5': '5rem',
      '6': '6rem',
      '7_5': '7.5rem',
      '9_5': '9.5rem',
      '12_5': '12.5rem',
      '15': '15rem',
      '17': '17rem',
      '18': '18rem',
      '19': '19rem',
    },
    gap: {
      xxSmall: '0.125rem',
      xSmall: '0.25rem',
      small: '0.5rem',
      smallMedium: '0.75rem',
      medium: '1rem',
      large: '1.5rem',
      xLarge: '2rem',
      xxLarge: '3rem',
      '0_313': '0.313rem',
      '0_38': '0.38rem',
      '0_44': '0.44rem',
      '0_69': '0.69rem',
      '1_13': '1.13rem',
      '1_25': '1.25rem',
      '1_75': '1.75rem',
      '2_5': '2.5rem',
      '3': '3rem',
      '3_5': '3.5rem',
      '3_75': '3.75rem',
      '4': '4rem',
      '4_5': '4.5rem',
      '5': '5rem',
      '6_5': '6.5rem',
      '10%': '10%',
      '5%': '5%',
    },
    borderRadius: {
      small: '0.25rem',
      medium: '0.5rem',
      large: '0.75rem',
      xLarge: '1.25rem',
      '0_063': '0.063rem',
      '0_375': '0.375rem',
      '0_625': '0.625rem',
      '1': '1rem',
      '1_25': '1.25rem',
      '1_5': '1.5rem',
      '1_75': '1.75rem',
      '1_1985': '1.1985rem',
      '2': '2rem',
      '2_125': '2.125rem',
      '2_5': '2.5rem',
      '2_75': '2.75rem',
      '3_125': '3.125rem',
      '4_5': '4.5rem',
      '5_25': '5.25rem',
      '5_5': '5.5rem',
    },
    width: {
      '0_25': '0.25rem',
      '0_4375': '0.4375rem',
      '0_75': '0.75rem',
      '0_875': '0.875rem',
      '1': '1rem',
      '1_125': '1.125rem',
      '1_25': '1.25rem',
      '1_5': '1.5rem',
      '1_75': '1.75rem',
      '1_875': '1.875rem',
      '2': '2rem',
      '2_125': '2.125rem',
      '2_25': '2.25rem',
      '2_27': '2.27rem',
      '2_32': '2.32rem',
      '2_5': '2.5rem',
      '3': '3rem',
      '3_5': '3.5rem',
      '3_375': '3.375rem',
      '4': '4rem',
      '4_125': '4.125rem',
      '4_375': '4.375rem',
      '4_5': '4.5rem',
      '4_688': '4.688rem',
      '5': '5rem',
      '5_5': '5.5rem',
      '5_75': '5.75rem',
      '6': '6rem',
      '6_375': '6.375rem',
      '6_5': '6.5rem',
      '6_75': '6.75rem',
      '7': '7rem',
      '7_25': '7.25rem',
      '7_5': '7.5rem',
      '7_7': '7.7rem',
      '8': '8rem',
      '8_0625': '8.0625rem',
      '8_5': '8.5rem',
      '8_875': '8.875rem',
      '10': '10rem',
      '11': '11rem',
      '11_12': '11.12rem',
      '12': '12rem',
      '12_375': '12.375rem',
      '12_5': '12.5rem',
      '13': '13rem',
      '13_375': '13.375rem',
      '13_75': '13.75rem',
      '14': '14rem',
      '15': '15rem',
      '15_625': '15.625rem',
      '16': '16rem',
      '17_5': '17.5rem',
      '18_75': '18.75rem',
      '19': '19rem',
      '20': '20rem',
      '21_625': '21.625rem',
      '22': '22rem',
      '22_5': '22.5rem',
      '23': '23rem',
      '25': '25rem',
      '36': '36rem',
      '18%': '18%',
      '28%': '28%',
      '30%': '30%',
      '32%': '32%',
      '33%': '33%',
      '35%': '35%',
      '37%': '37%',
      '38%': '38%',
      '43%': '43%',
      '50%': '50%',
      '58%': '58%',
      '59%': '59%',
      '65%': '65%',
      '80%': '80%',
      '85%': '85%',
      '100%': '100%',
      '30vw': '30vw',
      '100vw': '100vw',
    },
    height: {
      '0_25': '0.25rem',
      '0_4375': '0.4375rem',
      '0_36319': '0.36319rem',
      '0_43': '0.43rem',
      '0_63': '0.63rem',
      '0_75': '0.75rem',
      '0_875': '0.875rem',
      '1': '1rem',
      '1_18': '1.18rem',
      '1_125': '1.125rem',
      '1_25': '1.25rem',
      '1_375': '1.375rem',
      '1_5': '1.5rem',
      '1_75': '1.75rem',
      '1_875': '1.875rem',
      '2': '2rem',
      '2_125': '2.125rem',
      '2_25': '2.25rem',
      '2_27': '2.27rem',
      '2_5': '2.5rem',
      '2_75': '2.75rem',
      '3': '3rem',
      '3_35': '3.35rem',
      '3_375': '3.375rem',
      '3_5': '3.5rem',
      '3_8125': '3.8125rem',
      '4': '4rem',
      '4_5': '4.5rem',
      '5': '5rem',
      '5_43': '5.43rem',
      '6': '6rem',
      '6_5': '6.5rem',
      '6_75': '6.75rem',
      '6_875': '6.875rem',
      '7': '7rem',
      '7_5': '7.5rem',
      '7_6875': '7.6875rem',
      '7_813': '7.813rem',
      '8': '8rem',
      '8_5': '8.5rem',
      '8_75': '8.75rem',
      '8_8': '8.8rem',
      '8_875': '8.875rem',
      '9': '9rem',
      '9_8' : '9.8rem',
      '9_9': '9.9rem',
      '10': '10rem',
      '10_125': '10.125rem',
      '10_625': '10.625rem',
      '11': '11rem',
      '12': '12rem',
      '12_5': '12.5rem',
      '13_125': '13.125rem',
      '13_5': '13.5rem',
      '14': '14rem',
      '15': '15rem',
      '15_625': '15.625rem',
      '16_25': '16.25rem',
      '17_5': '17.5rem',
      '18_75': '18.75rem',
      '19_875': '19.875rem',
      '20': '20rem',
      '21':'21rem',
      '21_5': '21.5rem',
      '23': '23rem',
      '25': '25rem',
      '33_125': '33.125rem',
      '70%': '70%',
      '85%': '85%',
      '90%': '90%',
      '100%': '100%',
      '50vh': '50vh',
      '100vh': '100vh',
    },
    zIndex: {
      '1': 1,
      '11': 11,
    },
    customColors: {
      // Define colors common on both light theme and dark theme here.
      gray100: COMMON_COLORS.GREY_200,
      GREY_100: COMMON_COLORS.GREY_100,
      GREY_300: COMMON_COLORS.GREY_300,
      GREY_400: COMMON_COLORS.GREY_400,
      GREY_500: COMMON_COLORS.GREY_500,
      GREY_600: COMMON_COLORS.GREY_600,
      GREY_700: COMMON_COLORS.GREY_700,
      GREY_800: COMMON_COLORS.GREY_900,
    },
    gradients: {
      BLUE_100: 'linear-gradient(180deg, #CAF6F1 0%, #00D4BF 100%), #008578',
      highlightedNav: 'linear-gradient(0deg, #F9C935 0%, #FFDA57 100%)',
      GREY_100:
        'linear-gradient(180deg, rgba(255, 255, 255, 0.50) 0%, #FFF 100%)',
      GREY_200:
        'linear-gradient(180deg, rgba(255, 255, 255, 0.40) 0%, #FFF 100%)',
      GREEN_100: 'linear-gradient(180deg, #008578 0%, #004841 100%)',
      GREEN_200: 'linear-gradient(180deg, #CAF6F1 0%, #00D4BF 100%)',
      YELLOW_100: 'linear-gradient(180deg, #FFE79D 0%, #FFD551 100%)',
      YELLOW_200: 'linear-gradient(180deg, #FFF9E5 0%, #FFE799 100%)',
    },
    emptyStateBackground:
      'linear-gradient(91.14deg, rgba(255, 238, 185, 0) 3.5%, #FFEEB9 92.63%)',
  },
};

const lightThemeOptions = {
  ...commonSettings,
  palette: {
    mode: 'light',
    primary: {
      main: COMMON_COLORS.WHITE,
    },
    secondary: {
      main: COMMON_COLORS.YELLOW,
      light: LIGHT_MODE_COLORS.YELLOW_200,
      yellow_100: LIGHT_MODE_COLORS.YELLOW_100,
      yellow_200: COMMON_COLORS.YELLOW_200,
      yellow_300: COMMON_COLORS.YELLOW_300,
      yellow_400: COMMON_COLORS.YELLOW_400,
      yellow_500: COMMON_COLORS.YELLOW_500,
      yellow_600: COMMON_COLORS.YELLOW_600,
      yellow_700: COMMON_COLORS.YELLOW_700,
      yellow_800: COMMON_COLORS.YELLOW_800,
      yellow_900: COMMON_COLORS.YELLOW_900,
      primary: LIGHT_MODE_COLORS.YELLOW_300,
      secondary: LIGHT_MODE_COLORS.YELLOW_400,
      tertiary: LIGHT_MODE_COLORS.YELLOW_500,
      quaternary: COMMON_COLORS.YELLOW_800,
      quinary: LIGHT_MODE_COLORS.YELLOW_600,
      senary: LIGHT_MODE_COLORS.YELLOW_700,
      septenary: LIGHT_MODE_COLORS.YELLOW_800,
      octonary: LIGHT_MODE_COLORS.YELLOW_900,
    },
    tertiary: {
      main: LIGHT_MODE_COLORS.GREEN_100,
      dark: LIGHT_MODE_COLORS.GREEN_200,
      green: COMMON_COLORS.GREEN,
      green_200: COMMON_COLORS.GREEN_200,
      light: LIGHT_MODE_COLORS.GREEN_300,
      primary: LIGHT_MODE_COLORS.GREEN_600,
      secondary: LIGHT_MODE_COLORS.GREEN_400,
      tertiary: LIGHT_MODE_COLORS.GREEN_500,
    },
    quaternary: {
      main: LIGHT_MODE_COLORS.BLUE_100,
      light: LIGHT_MODE_COLORS.BLUE_400,
      secondary: LIGHT_MODE_COLORS.BLUE_500,
    },
    quinary: {
      main: COMMON_COLORS.RED,
      red_100: COMMON_COLORS.RED_100,
      red_200: COMMON_COLORS.RED_200,
      red_300: COMMON_COLORS.RED_300,
      red_400: COMMON_COLORS.RED_400,
      light: LIGHT_MODE_COLORS.RED_100,
      dark: LIGHT_MODE_COLORS.RED_200,
      secondary: LIGHT_MODE_COLORS.RED_300,
    },
    senary: {
      main: LIGHT_MODE_COLORS.BLUE_200,
      light: LIGHT_MODE_COLORS.BLUE_300,
      dark: LIGHT_MODE_COLORS.BLUE_200,
    },
    background: {
      default: COMMON_COLORS.WHITE,
      paper: LIGHT_MODE_COLORS.LIGHT_GREY_100,
      secondary: LIGHT_MODE_COLORS.WHITE_100,
      tertiary: LIGHT_MODE_COLORS.WHITE_200,
      quaternary: LIGHT_MODE_COLORS.WHITE_300,
      quinary: LIGHT_MODE_COLORS.WHITE_400,
      senary: LIGHT_MODE_COLORS.BLACK_500,
      septenary: LIGHT_MODE_COLORS.BLACK_600,
      octonary: LIGHT_MODE_COLORS.LIGHT_GREY_400,
      nonary: LIGHT_MODE_COLORS.BLACK_700,
      denary: LIGHT_MODE_COLORS.BLACK_800,
      elevenary: LIGHT_MODE_COLORS.LIGHT_GREY_500,
      twelvenary: LIGHT_MODE_COLORS.BLACK_900,
      thirteenary: LIGHT_MODE_COLORS.WHITE_500,
      fourteenary: LIGHT_MODE_COLORS.LIGHT_GREY_600,
      fifteenary: LIGHT_MODE_COLORS.LIGHT_GREY_700,
      sixteenary: LIGHT_MODE_COLORS.LIGHT_GREY_800,
      seventeenary: LIGHT_MODE_COLORS.LIGHT_GREY_900,
      eighteenary: LIGHT_MODE_COLORS.WHITE_600,
      nineteenary: LIGHT_MODE_COLORS.WHITE_700,
      twenteenary: LIGHT_MODE_COLORS.LIGHT_GREY_1000,
      twentyOnenary: LIGHT_MODE_COLORS.LIGHT_GREY_1100,
      twentyTwoary: LIGHT_MODE_COLORS.LIGHT_GREY_1200,
      grey_200: LIGHT_MODE_COLORS.LIGHT_GREY_200,
      grey_300: LIGHT_MODE_COLORS.LIGHT_GREY_300,
      dark_grey_100: LIGHT_MODE_COLORS.DARK_GREY_100,
      black_1100: LIGHT_MODE_COLORS.BLACK_1100,
      black_1200: LIGHT_MODE_COLORS.BLACK_1200,
      black: COMMON_COLORS.BLACK,
      YELLOW: LIGHT_MODE_COLORS.YELLOW_800,
    },
    text: {
      primary: LIGHT_MODE_COLORS.BLACK_100,
      secondary: COMMON_COLORS.BLACK,
      tertiary: LIGHT_MODE_COLORS.BLACK_200,
      quaternary: LIGHT_MODE_COLORS.DARK_GREY_100,
      quinary: LIGHT_MODE_COLORS.BLACK_300,
      senary: LIGHT_MODE_COLORS.BLACK_400,
      septenary: LIGHT_MODE_COLORS.DARK_GREY_200,

      octonary: COMMON_COLORS.BLUE,
      nonary: COMMON_COLORS.GREY_800,
      denary: LIGHT_MODE_COLORS.BLACK_1000,
      elevenary: COMMON_COLORS.WHITE,
      slate: COMMON_COLORS.SLATE,
      slate_light: COMMON_COLORS.SLATE_LIGHT,
    },
  },
};

const darkThemeOptions = {
  ...commonSettings,
  palette: {
    mode: 'dark',
    primary: {
      main: COMMON_COLORS.BLACK,
    },
    tertiary: {
      main: LIGHT_MODE_COLORS.GREEN_100,
    },
    background: {
      default: COMMON_COLORS.BLACK,
      paper: COMMON_COLORS.BLACK,
    },
    text: {
      primary: COMMON_COLORS.WHITE,
      secondary: COMMON_COLORS.WHITE,
    },
  },
};

const lightTheme = createTheme(lightThemeOptions as ThemeOptions);
const darkTheme = createTheme(darkThemeOptions as ThemeOptions);

export { lightTheme, darkTheme, lightThemeOptions, darkThemeOptions };
